import { uniq, size, isEmpty } from 'lodash';
import { encode } from 'js-base64';

import { SEARCH_TYPE_NAMES } from 'src/constants/search';
import { objToQueryString } from 'src/utils/uriUtils';
import {
  getHotelExtraParams,
  getCheapestDate,
  getSuggestedTour,
} from '../utils';

// from_city:    from[:city][:id],
// from_country: from[:country][:id],
// to_city:      to[:city][:id],
// to_country:   to[:country][:id],
// hotel_ids:    to[:hotel_ids].to_a.join(','),
// start_date:   dates,
// flex_dates:   (flex ? 1 : 0) || options[:flex_dates],
// nights:       [nights[:from], nights[:to]].join(','),
// adults:       adults,
// kids:         kids[:count],
// kidsAges:     kids[:ages].join(','),
// stars:        strs,
// from_price:   options[:from_price],
/**
 *
 * @param {object} params
 */

const buildRequestId = (params) => {
  const requestId = encode(Object.values(params).join('|'));

  return requestId;
};

const buildSuggestedHotelURL = ({
  packageInfo,
  hotelParams,
  normalizeLink,
  date,
  price,
  nightsCount,
  filters,
}) => {
  const dates = {};
  dates[date] = price;

  const queryParams = getHotelExtraParams(
    {
      ...packageInfo,
      dates,
      price,
      min_price_nights: nightsCount,
    },
    filters,
  );
  const query = objToQueryString({
    ...hotelParams,
    ...queryParams,
    nights: nightsCount,
  });
  return `${normalizeLink}${query ? `?${query}` : ''}`;
};

/**
 * Util to build suggested dates/tours;
 * @param {object} params requestId/normalizeLink/adjacentTours/packageInfo/filters
 * @param {string} currencyCode
 * @returns suggested dates/tours(same shit)
 */
const buildSuggestedDates = (params, currencyCode) => {
  const { packageInfo, hotelParams, normalizeLink, adjacent_tours, filters } =
    params;
  const suggestedDates = adjacent_tours.map((tour) => ({
    ...getSuggestedTour(tour),
    currencyCode,
    href: buildSuggestedHotelURL({
      packageInfo,
      hotelParams,
      normalizeLink,
      date: tour.start_date,
      price: tour.price,
      nightsCount: tour.duration,
      filters,
    }),
  }));

  return suggestedDates;
};

/**
 * Util to build hotel href
 * @param {object} packageInfo base hotel info
 * @param {string} normalizeLink hotel link
 * @param {object} hotelParams hotel search params to build url
 * @param {object} filters filters to build url
 * @returns hotel href
 */
const buildHotelHref = (packageInfo, normalizeLink, hotelParams, filters) => {
  const queryParams = getHotelExtraParams(packageInfo, filters);
  const query = objToQueryString({ ...queryParams });
  const searchParamStr = objToQueryString(hotelParams);
  const hotelHref = `${normalizeLink}${
    query ? `?${searchParamStr}&${query}` : ''
  }`;

  return hotelHref;
};

/**
 * Util to build hotel object
 * @param {object} hotelInfo serverside Hotel info
 * @param {object} searchParams search params
 * @param {string} currencyCode currency code
 * @param {object} filters filters to build url
 * @returns hotel href
 */
export const buildHotel = (hotelInfo, searchParams, currencyCode, filters) => {
  const {
    hotel,
    surcharge,
    destination,
    min_price_nights,
    min_price: price,
    dates,
    adjacent_tours = [],
  } = hotelInfo;
  const {
    id,
    images_count: imagesCount,
    images,
    region_name: regionName,
    city,
    stars,
  } = hotel;

  const { adults, flex_dates, kids } = searchParams;
  const kidsAges = 'ages' in kids ? kids.ages : kids;
  const { search_type: searchType } =
    searchParams.presenterParams || searchParams;

  const departure =
    searchType === SEARCH_TYPE_NAMES.HOTEL
      ? { country_id: null, text: null, slug_name: 'Any-RU', city_id: 'Any' }
      : hotelInfo.departure;

  const normalizeCity = city.name_ru || city;
  const location = uniq([normalizeCity, regionName, destination.country_name])
    .filter(Boolean)
    .join(', ');
  const touristsCount = adults + size(kidsAges);

  const packageInfo = {
    touristsCount,
    price,
    min_price_nights,
    dates,
    surcharge,
  };

  const hotelParams = {
    adults,
    kids: kidsAges ? kidsAges.length : 0,
    kids_ages: kidsAges ? kidsAges.join(',') : '',
    nights: min_price_nights,
    from: departure?.slug_name || 'Moscow-RU',
    search_type: searchType,
  };

  const normalizeLink = hotel.link || hotel.public_url;
  const hotelHref = buildHotelHref(
    packageInfo,
    normalizeLink,
    hotelParams,
    filters,
  );

  const dataForRequestId = {
    from_city: departure.city_id,
    from_country: departure.country_id,
    to_city: destination.city_id,
    to_country: destination.country_id,
    hotel_ids: String(id),
    start_date: getCheapestDate(dates),
    flex_dates: Number(flex_dates),
    nights: String(min_price_nights),
    adults: String(adults),
    kids: String(kidsAges ? kidsAges.length : 0),
    kidsAges: kidsAges ? kidsAges.join(',') : '',
    stars,
    from_price: price,
  };

  const request_id = buildRequestId(dataForRequestId);

  const wishlistData = {
    type: 'hotel',
    link: `${normalizeLink}`,
    hotel_id: id,
    request_id,
    price,
  };

  const suggestedDates = buildSuggestedDates(
    {
      packageInfo,
      hotelParams,
      normalizeLink,
      adjacent_tours,
      filters,
    },
    currencyCode,
  );

  const serializedHotel = {
    ...hotelInfo,
    suggestedDates,
    hotel,
    location,
    price,
    surcharge,
    hotelHref,
    departure: departure.text,
    touristsCount,
    nights: min_price_nights,
    date: getCheapestDate(dates),
    hasLtExtras: false,
    isViewed: false,
    images: {
      list: images.map((image) => ({ jpg: image.x500, webp: image.webp_x620 })),
      count: imagesCount,
      onSlideChange: () => '',
    },
    data: wishlistData,
    city: normalizeCity,
    regionName,
    country: destination.country_id,
  };

  return serializedHotel;
};

/**
 * Getting segments for linkedMonths:
 * @param {number} price Hotel price
 * @param {number} lowestPrice Lowest price between all hotels
 * @returns segment
 */
const getSegment = (price, lowestPrice) => {
  if (!price) return 0;

  const ratio = price / (lowestPrice / 100) - 100;
  if (ratio <= 3) return 5;
  if (ratio <= 10) return 4;
  if (ratio <= 15) return 3;

  return 1;
};

/**
 * Function for serializing months:
 * @param {Month} linkedMonths Linked month with segments
 * @returns Serialized months
 */
export const buildMonths = (linkedMonths) => {
  if (isEmpty(linkedMonths)) return;

  const lowestPrice = linkedMonths.reduce(
    (min, month) => (month.price && month.price < min ? month.price : min),
    linkedMonths[0].price,
  );

  // eslint-disable-next-line consistent-return
  return linkedMonths.map((month) => {
    if (!month) return null;
    const { price } = month;
    const newMonth = { ...month };
    newMonth.segment = getSegment(price, lowestPrice);
    return newMonth;
  });
};
